import type { SponsorDto, SponsorshipDto } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  BENEFIT_STATUS_LABELS,
  BENEFIT_TYPE_LABELS,
  SPONSORSHIP_STATUS_LABELS,
  formatDate,
} from '../lib/format';
import { Amount } from '../components/Amount';
import { ContractDialog } from './SponsoringPage';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorBlock,
  LoadingBlock,
  SectionTitle,
  Input,
  Select,
  useToast,
} from '../components/ui';

export function SponsorshipDetailPage(): JSX.Element {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = useAuth();
  const [editOpen, setEditOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['sponsorships', 'detail', id],
    queryFn: () => api.get<SponsorshipDto>(`/api/sponsorships/${id}`),
    enabled: !!id,
  });

  const { data: sponsors } = useQuery({
    queryKey: ['sponsors'],
    queryFn: () => api.get<SponsorDto[]>('/api/sponsors'),
    enabled: editOpen,
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['sponsorships'] });
    await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const updateBenefit = useMutation({
    mutationFn: ({
      benefitId,
      ...patch
    }: {
      benefitId: string;
      status?: string;
      deliveredCount?: number;
      description?: string | null;
    }) => api.patch(`/api/sponsorship-benefits/${benefitId}`, patch),
    onSuccess: invalidate,
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Änderung fehlgeschlagen', 'error'),
  });

  const removeContract = useMutation({
    mutationFn: () => api.del(`/api/sponsorships/${id}`),
    onSuccess: async () => {
      await invalidate();
      toast.show('Sponsorvertrag entfernt');
      navigate('/sponsoring');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Löschen fehlgeschlagen', 'error'),
  });

  const createTask = useMutation({
    mutationFn: (benefitId: string) =>
      api.post<{ taskId: string }>(`/api/sponsorship-benefits/${benefitId}/task`),
    onSuccess: async () => {
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.show('Aufgabe zur Sponsorleistung erstellt');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Aufgabe fehlgeschlagen', 'error'),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} />;
  if (!data) return <ErrorBlock error={new Error('Vertrag nicht gefunden')} />;

  return (
    <div className="space-y-4">
      <Link to="/sponsoring" className="text-sm text-brand-700 hover:underline">
        ← Zurück zum Sponsoring
      </Link>

      <Card>
        <CardBody className="space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">{data.sponsorName}</h1>
              <p className="text-sm text-slate-600">{data.title}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                tone={
                  data.status === 'ACTIVE'
                    ? 'success'
                    : data.status === 'EXPIRING'
                      ? 'warning'
                      : 'neutral'
                }
              >
                {SPONSORSHIP_STATUS_LABELS[data.status] ?? data.status}
              </Badge>
              {data.detailsVisible && can('sponsoring_contracts', 'update') && (
                <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
                  Bearbeiten
                </Button>
              )}
              {data.detailsVisible && can('sponsoring_contracts', 'delete') && (
                <Button
                  variant="danger"
                  size="sm"
                  loading={removeContract.isPending}
                  onClick={() => {
                    if (window.confirm(`Vertrag "${data.title}" wirklich entfernen?`)) {
                      removeContract.mutate();
                    }
                  }}
                >
                  Löschen
                </Button>
              )}
            </div>
          </div>

          <dl className="grid gap-3 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-400">Laufzeit</dt>
              <dd className="text-slate-700">
                {formatDate(data.validFrom)} – {formatDate(data.validUntil)}
              </dd>
            </div>
            {data.detailsVisible && data.amountBand && (
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-400">Betrag</dt>
                <dd>
                  <Amount amount={data.amount} band={data.amountBand} currency={data.currency} />
                </dd>
              </div>
            )}
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-400">Bereiche</dt>
              <dd className="text-slate-700">
                {data.organisationUnits.map((u) => u.name).join(', ')}
              </dd>
            </div>
          </dl>

          {data.detailsVisible && data.daysUntilEnd <= 60 && (
            <p
              className={
                data.daysUntilEnd < 0
                  ? 'rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800'
                  : 'rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800'
              }
            >
              {data.daysUntilEnd < 0
                ? `Der Vertrag ist seit ${Math.abs(data.daysUntilEnd)} Tagen abgelaufen.`
                : `Der Vertrag läuft in ${data.daysUntilEnd} Tagen aus. Eine Aufgabe zur Verlängerung wurde automatisch erstellt.`}
            </p>
          )}

          {data.notes && <p className="whitespace-pre-wrap text-sm text-slate-700">{data.notes}</p>}

          {!data.detailsVisible && (
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
              🔒 Vertragsinhalt, Leistungen und Beträge sind vertraulich. Fragen zum Sponsor bitte an die
              betreuende Person richten.
            </p>
          )}
        </CardBody>
      </Card>

      {data.detailsVisible && (
      <Card>
        <CardBody>
          <SectionTitle
            title="Vereinbarte Leistungen"
            subtitle={`${data.benefits.filter((b) => b.status === 'DELIVERED').length} von ${data.benefits.length} erfüllt`}
          />
          {data.benefits.length === 0 ? (
            <p className="text-sm text-slate-500">Keine Leistungen hinterlegt.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.benefits.map((benefit) => (
                <li key={benefit.id} className="flex flex-wrap items-center gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-800">
                      {benefit.offerTitle ?? BENEFIT_TYPE_LABELS[benefit.type] ?? benefit.type}
                      {benefit.quantity > 1 && (
                        <span className="ml-1 text-sm text-slate-500">
                          ({benefit.deliveredCount}/{benefit.quantity})
                        </span>
                      )}
                    </p>
                    <BenefitNote
                      note={benefit.description}
                      editable={can('sponsoring_contracts', 'update')}
                      saving={updateBenefit.isPending}
                      onSave={(description) => updateBenefit.mutateAsync({ benefitId: benefit.id, description })}
                    />
                    {benefit.dueAt && (
                      <p className="text-xs text-slate-500">
                        Fällig bis {formatDate(benefit.dueAt)}
                      </p>
                    )}
                  </div>
                  <Badge
                    tone={
                      benefit.status === 'DELIVERED'
                        ? 'success'
                        : benefit.status === 'OPEN'
                          ? 'warning'
                          : 'neutral'
                    }
                  >
                    {BENEFIT_STATUS_LABELS[benefit.status] ?? benefit.status}
                  </Badge>
                  {can('sponsoring_contracts', 'update') && (
                    <>
                      {/* Teilerfuellung: haeufigster Fall bei mehrteiligen
                          Leistungen wie "4 Instagram-Beitraege". */}
                      {benefit.quantity > 1 && benefit.status !== 'CANCELLED' && (
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={
                            benefit.deliveredCount >= benefit.quantity || updateBenefit.isPending
                          }
                          onClick={() =>
                            updateBenefit.mutate({
                              benefitId: benefit.id,
                              deliveredCount: benefit.deliveredCount + 1,
                            })
                          }
                        >
                          +1 erbracht
                        </Button>
                      )}
                      <Select
                        value={benefit.status}
                        onChange={(e) =>
                          updateBenefit.mutate({ benefitId: benefit.id, status: e.target.value })
                        }
                        className="w-auto"
                        aria-label={`Status ${BENEFIT_TYPE_LABELS[benefit.type] ?? benefit.type}`}
                      >
                        {Object.entries(BENEFIT_STATUS_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </Select>
                    </>
                  )}
                  {benefit.taskId ? (
                    <Link
                      to={`/aufgaben/${benefit.taskId}`}
                      className="text-sm text-brand-700 hover:underline"
                    >
                      Aufgabe ansehen
                    </Link>
                  ) : (
                    can('tasks', 'create') && can('sponsoring_contracts', 'update') && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => createTask.mutate(benefit.id)}
                        loading={createTask.isPending}
                      >
                        Aufgabe erstellen
                      </Button>
                    )
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
      )}

      <ContractDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        sponsors={sponsors ?? []}
        sponsorship={data}
      />
    </div>
  );
}

/** Hinweis zu einer Leistung - direkt an der Leistung bearbeitbar. */
function BenefitNote({
  note,
  editable,
  saving,
  onSave,
}: {
  note: string | null;
  editable: boolean;
  saving: boolean;
  onSave: (note: string | null) => Promise<unknown>;
}): JSX.Element | null {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(note ?? '');

  if (editing) {
    return (
      <form
        className="mt-1 flex flex-wrap gap-2"
        onSubmit={async (e) => {
          e.preventDefault();
          await onSave(value.trim() || null);
          setEditing(false);
        }}
      >
        <Input
          aria-label="Hinweis zur Leistung"
          value={value}
          maxLength={500}
          autoFocus
          onChange={(e) => setValue(e.target.value)}
          className="min-w-0 flex-1 text-sm"
        />
        <Button type="submit" size="sm" loading={saving}>Speichern</Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => { setValue(note ?? ''); setEditing(false); }}>
          Abbrechen
        </Button>
      </form>
    );
  }

  if (!note && !editable) return null;
  return (
    <p className="text-sm text-slate-600">
      {note && <span className="whitespace-pre-wrap">{note}</span>}
      {editable && (
        <button
          type="button"
          className={note ? 'ml-2 text-xs text-brand-700 hover:underline' : 'text-xs text-brand-700 hover:underline'}
          onClick={() => { setValue(note ?? ''); setEditing(true); }}
        >
          {note ? 'Hinweis ändern' : '+ Hinweis'}
        </button>
      )}
    </p>
  );
}
